import { useEffect, useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { m } from 'motion/react';
import { Monitor, Moon, Share2, Sun, X } from 'lucide-react';
import { isTopDialog, useDialog } from '../hooks/useDialog';
import { useT } from '../hooks/useT';
import type { Settings } from '../hooks/useSettings';
import { LANGUAGES } from '../utils/i18n';
import { BACKGROUND_THEMES, type BackgroundTheme } from '../utils/themeStyles';
import { chip } from '../utils/ui';
import { AnalyticsToggle } from './AnalyticsConsent';
import { BrandLink } from './BrandLink';
import { InstallCard } from './InstallCard';
import { VersionInfo } from './VersionInfo';

const MODES = [
  { id: 'light', key: 'modeLight', Icon: Sun },
  { id: 'dark', key: 'modeDark', Icon: Moon },
  { id: 'auto', key: 'modeAuto', Icon: Monitor },
] as const;

interface SettingsMenuProps {
  settings: Settings;
  onClose: () => void;
  onShare: () => void;
  /** Eigen onderdelen van de app (bv. extra instellingen) komen hier in het menu */
  children?: ReactNode;
}

/** Zijmenu (hamburger): weergave, taal, delen, installeren, versie en merk. Sluit met Escape of een klik ernaast. */
export function SettingsMenu({ settings, onClose, onShare, children }: SettingsMenuProps) {
  const { t } = useT();
  const titleId = useId();
  const panelRef = useDialog<HTMLDivElement>(true);
  const { language, setLanguage, mode, setMode, background, setBackground } = settings;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && isTopDialog(panelRef.current) && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, panelRef]);

  return createPortal(
    <m.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
      className="fixed inset-0 z-[120] bg-slate-950/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <m.div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        initial={{ x: 40, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="ml-auto flex h-full w-full max-w-sm flex-col overflow-y-auto bg-slate-50 p-6 text-slate-900 shadow-xl outline-none dark:border-l dark:border-white/10 dark:bg-slate-900 dark:text-slate-100"
      >
        <div className="mb-6 flex items-center justify-between gap-3">
          <h2 id={titleId} className="text-lg font-bold">{t('menuTitle')}</h2>
          <button
            onClick={onClose}
            aria-label={t('closeBtn')}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-black/5 transition-all duration-150 active:scale-95 dark:bg-slate-800/80"
          >
            <X size={20} aria-hidden />
          </button>
        </div>

        <div className="flex-1 space-y-6 pb-6">
          <section className="space-y-4">
            <h3 className="font-semibold">{t('settingsTitle')}</h3>
            <div className="flex flex-wrap gap-2">
              {MODES.map(({ id, key, Icon }) => (
                <button key={id} onClick={() => setMode(id)} aria-pressed={mode === id} className={chip(mode === id)}>
                  <Icon size={18} aria-hidden /> {t(key)}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              {(Object.keys(BACKGROUND_THEMES) as BackgroundTheme[]).map((id) => (
                <button
                  key={id}
                  onClick={() => setBackground(id)}
                  aria-label={BACKGROUND_THEMES[id].label}
                  aria-pressed={background === id}
                  className={`h-11 w-11 rounded-2xl transition-all duration-150 active:scale-95 ${BACKGROUND_THEMES[id].swatch} ${
                    background === id ? 'ring-2 ring-slate-900 ring-offset-2 dark:ring-slate-100 dark:ring-offset-slate-900' : ''
                  }`}
                />
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-slate-600 dark:text-slate-400">{t('languageLabel')}</span>
              {LANGUAGES.map((l) => (
                <button key={l} onClick={() => setLanguage(l)} aria-pressed={language === l} className={chip(language === l)}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <button onClick={onShare} className={chip(false)}>
              <Share2 size={18} aria-hidden /> {t('deelWens')}
            </button>
          </section>

          {children}

          <AnalyticsToggle />

          <section className="border-t border-black/5 pt-6 dark:border-white/10">
            <InstallCard />
          </section>
        </div>

        <div className="space-y-3 border-t border-black/5 pt-5 dark:border-white/10">
          <VersionInfo />
          <p className="text-xs text-slate-600 dark:text-slate-400">
            <BrandLink />
          </p>
        </div>
      </m.div>
    </m.div>,
    document.body,
  );
}
